import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { getReportsByDivision } from '../reports/report-definitions';
import { Division, ReportDefinition } from '../reports/report.model';
import { ApprovalService } from '../approvals/approval.service';
import { AuthService } from '../../auth/auth.service';
import { NotificationService } from '../../core/notification.service';

/**
 * Reports hub for a division: lists its available reports as cards that open
 * into the report viewer, and lets the division send its reports for approval.
 */
@Component({
  selector: 'app-reports-hub',
  standalone: false,
  templateUrl: './reports-hub.component.html',
  styleUrl: './reports-hub.component.scss'
})
export class ReportsHubComponent {
  /** Division label, e.g. "Actuary" or "Finance". */
  readonly divisionName: string;
  /** Division route slug, e.g. "actuary". */
  readonly division: Division;
  readonly reports: ReportDefinition[];

  /** The confirmation dialog is open while this is true. */
  confirmVisible = false;
  sending = false;
  sendError: string | null = null;

  constructor(
    route: ActivatedRoute,
    private readonly approvals: ApprovalService,
    private readonly auth: AuthService,
    private readonly notifications: NotificationService
  ) {
    this.divisionName = route.snapshot.data['division'] ?? 'Reports';
    this.division = this.divisionName.toLowerCase() as Division;
    this.reports = getReportsByDivision(this.division);
  }

  /** Opens the confirmation dialog — sending mail is not a one-click action. */
  openSendConfirm(): void {
    this.sendError = null;
    this.confirmVisible = true;
  }

  /** Closes the confirmation dialog without sending. */
  cancelSend(): void {
    if (this.sending) {
      return;
    }
    this.confirmVisible = false;
  }

  /** Notifies the division's approvers that these reports are ready. */
  confirmSend(): void {
    this.sending = true;
    this.sendError = null;

    this.approvals
      .sendForApproval({
        department: this.divisionName,
        requestedBy: this.auth.getUsername()
      })
      .subscribe({
        next: (response) => {
          this.sending = false;
          this.confirmVisible = false;
          this.notifications.success(
            'Sent for approval',
            response?.message ??
              `${this.divisionName} reports sent for approval.`
          );
        },
        error: (error: HttpErrorResponse) => {
          this.sending = false;
          // The API explains *why* it refused (no recipients configured, site URL
          // missing), and that's the actionable part — surface it when present.
          this.sendError =
            error?.error?.message ??
            'Could not send the approval notification. Please try again.';
        }
      });
  }
}
